/**
 * Utilitaires pour les calculs de TVA
 * Conforme EN 16931 avec ventilation par taux
 *
 * ⚠️ DEPRECATION NOTICE (Phase 3 - Sprint Financial Architecture)
 * @see docs/SPRINT_FINANCIAL_ARCHITECTURE.md
 *
 * Les fonctions de CALCUL de ce fichier sont deprecated et seront supprimées.
 * Utilisez les données pré-calculées du backend via:
 * - Invoice/Quote: totalHT, totalTVA, totalTTC depuis le backend
 * - Stats: GET /api/v1/business-metrics/unified
 *
 * Fonctions deprecated:
 * - calculateTotalsWithVat() → Utiliser backend InvoiceService.calculateTotals()
 *   (conservée temporairement car utilisée par useDocumentEdit.ts)
 *
 * Fonctions CONSERVÉES (formatting only - pas de calcul):
 * - isVatExempt(), getVatExemptionMention() - régime TVA
 * - formatCurrency(), formatVatRate(), formatDate(), formatDateLong() - formatting
 */

export interface VatBreakdownItem {
  rate: number; // Taux de TVA en %
  baseHT: number; // Base HT (somme des lignes à ce taux)
  vatAmount: number; // Montant de TVA
  totalTTC: number; // Total TTC pour ce taux
  itemsCount: number; // Nombre de lignes concernées
}

export interface InvoiceItem {
  id?: string | number;
  description: string;
  quantity: number;
  price?: number; // Prix unitaire (alias pour unitPrice)
  unitPrice?: number; // Prix unitaire (utilisé dans QuoteTemplate)
  total?: number; // Total HT net de remise ligne
  vatRate?: number; // Taux de TVA
  taxPercent?: number; // Taux de TVA (format Prisma V2)
  vatAmount?: number; // Montant TVA calculé
}

export interface VatCalculationResult {
  subtotal: number; // Total HT
  vatBreakdown: VatBreakdownItem[]; // Ventilation par taux
  totalVat: number; // Total TVA
  total: number; // Total TTC
}

/**
 * Obtenir le prix unitaire d'un item (gère price et unitPrice)
 */
function getItemPrice(item: InvoiceItem): number {
  return item.price ?? item.unitPrice ?? 0;
}

/**
 * Calculer les totaux avec ventilation TVA
 * Conforme EN 16931 : remise répartie au prorata par taux de TVA
 * @deprecated Use backend InvoiceService.calculateTotals() or QuoteService.calculateTotals() instead
 */
export function calculateTotalsWithVat(
  items: InvoiceItem[],
  discountAmount: number = 0
): VatCalculationResult {
  // Grouper les items par taux de TVA
  // Support des formats legacy (vat_rate) ET Prisma V2 (taxPercent)
  const itemsByVatRate = items.reduce((acc, item) => {
    const rate = item.taxPercent ?? item.vatRate ?? 20; // Défaut 20% si non spécifié
    if (!acc[rate]) {
      acc[rate] = [];
    }
    acc[rate].push(item);
    return acc;
  }, {} as Record<number, InvoiceItem[]>);

  // Calculer pour chaque taux de TVA AVANT remise
  const vatBreakdownBeforeDiscount: VatBreakdownItem[] = Object.entries(itemsByVatRate)
    .map(([rateStr, rateItems]) => {
      const rate = parseFloat(rateStr);

      // Calculer la base HT pour ce taux AVANT remise document
      // Utilise item.total (net de remise ligne) si disponible, sinon qty × prix
      const baseHT = rateItems.reduce((sum, item) => {
        if (item.total !== undefined && item.total !== null) {
          // Round intermediate accumulation to prevent float drift
          return Math.round((sum + item.total) * 100) / 100;
        }
        const price = getItemPrice(item);
        // Round intermediate accumulation to prevent float drift
        return Math.round((sum + (item.quantity * price)) * 100) / 100;
      }, 0);

      return {
        rate,
        baseHT: Number(baseHT.toFixed(2)),
        vatAmount: 0, // Sera calculé après remise
        totalTTC: 0, // Sera calculé après remise
        itemsCount: rateItems.length,
      };
    })
    .sort((a, b) => b.rate - a.rate); // Trier par taux décroissant

  // Total HT AVANT remise
  // Round intermediate accumulation to prevent float drift
  const subtotalBeforeDiscount = Math.round(vatBreakdownBeforeDiscount.reduce((sum, vat) => sum + vat.baseHT, 0) * 100) / 100;

  // Répartir la remise au prorata de chaque base HT
  let remainingDiscount = discountAmount;
  const vatBreakdown: VatBreakdownItem[] = vatBreakdownBeforeDiscount.map((vat, index, array) => {
    let discountForRate: number;

    // Pour le dernier taux, ajuster pour éviter les erreurs d'arrondi
    if (index === array.length - 1) {
      discountForRate = remainingDiscount;
    } else {
      // Remise au prorata pour ce taux
      discountForRate = subtotalBeforeDiscount > 0
        ? Number((discountAmount * vat.baseHT / subtotalBeforeDiscount).toFixed(2))
        : 0;
      remainingDiscount -= discountForRate;
    }

    // Base HT après remise
    const baseHTAfterDiscount = Math.max(0, vat.baseHT - discountForRate);

    // Recalculer la TVA sur la base après remise
    // Round intermediate calculation to prevent float accumulation
    const vatAmount = Math.round(baseHTAfterDiscount * (vat.rate / 100) * 100) / 100;

    // Total TTC pour ce taux
    // Round to prevent float drift
    const totalTTC = Math.round((baseHTAfterDiscount + vatAmount) * 100) / 100;

    return {
      ...vat,
      baseHT: Number(baseHTAfterDiscount.toFixed(2)),
      vatAmount: Number(vatAmount.toFixed(2)),
      totalTTC: Number(totalTTC.toFixed(2)),
    };
  });

  // Calculer les totaux finaux
  // Round each accumulation to prevent float drift from multiple breakdown items
  const subtotal = Math.round(vatBreakdown.reduce((sum, vat) => sum + vat.baseHT, 0) * 100) / 100;
  const totalVat = Math.round(vatBreakdown.reduce((sum, vat) => sum + vat.vatAmount, 0) * 100) / 100;
  const total = Math.round(vatBreakdown.reduce((sum, vat) => sum + vat.totalTTC, 0) * 100) / 100;

  return {
    subtotal: Number(subtotal.toFixed(2)),
    vatBreakdown,
    totalVat: Number(totalVat.toFixed(2)),
    total: Number(total.toFixed(2)),
  };
}

/**
 * Vérifier si une entreprise est en franchise de TVA
 */
export function isVatExempt(vatRegime: string | undefined): boolean {
  return ['franchise_base', 'exempt', 'intra_eu', 'export'].includes(vatRegime || '');
}

/**
 * Obtenir la mention légale d'exonération selon le régime
 */
export function getVatExemptionMention(vatRegime: string | undefined, customMention?: string): string {
  if (customMention) return customMention;

  switch (vatRegime) {
    case 'franchise_base':
      return 'TVA non applicable, article 293 B du CGI';
    case 'intra_eu':
      return 'Autoliquidation par le client - Article 283-2 du CGI';
    case 'export':
      return 'Exonération de TVA - Article 262 I du CGI';
    case 'exempt':
      return 'Exonération de TVA';
    default:
      return '';
  }
}

/** Codes ISO 3166-1 alpha-2 des États membres de l'UE (hors France) */
const EU_COUNTRY_CODES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'DE',
  'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL',
  'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
]);

/**
 * Détecte si un document est en auto-liquidation intracom
 * Client UE (hors FR) + TVA à 0 → art. 262 ter I du CGI
 */
export function isIntraCommunity(clientCountryCode?: string | null, totalTVA?: number): boolean {
  return !!(
    clientCountryCode &&
    EU_COUNTRY_CODES.has(clientCountryCode.toUpperCase()) &&
    (totalTVA === 0 || totalTVA === undefined)
  );
}

/**
 * Résout la mention légale d'exemption TVA d'un document
 * Prend en compte le régime de la société ET le contexte intracom
 */
export function resolveDocumentVatExemptArticle(
  vatRegime?: string,
  clientCountryCode?: string | null,
  totalTVA?: number
): string | undefined {
  // 1. Régime de la société (franchise en base, export, etc.)
  if (vatRegime && ['franchise_base', 'exempt', 'intra_eu', 'export'].includes(vatRegime)) {
    return getVatExemptionMention(vatRegime) || undefined;
  }

  // 2. Auto-liquidation intracom (client UE hors FR + TVA 0)
  if (isIntraCommunity(clientCountryCode, totalTVA)) {
    return 'Exonération de TVA - art. 262 ter I du CGI';
  }

  return undefined;
}

/**
 * Formater un montant en devise
 * Note: Remplace l'espace insécable (U+00A0) par un espace normal
 * pour éviter les problèmes de rendu dans les PDF (html2canvas)
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount || 0).replace(/\u00A0/g, ' ');
}

/**
 * Formater un taux de TVA
 */
export function formatVatRate(rate: number): string {
  return `${rate.toLocaleString('fr-FR')}%`;
}

/**
 * Formater une date au format français court (jj/mm/aaaa)
 * Accepte : Date, string ISO (yyyy-mm-dd), timestamp
 */
export function formatDate(date: Date | string | number | null | undefined): string {
  if (!date) return '';

  const d = typeof date === 'string' || typeof date === 'number'
    ? new Date(date)
    : date;

  // Vérifier que la date est valide
  if (isNaN(d.getTime())) return '';

  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(d);
}

/**
 * Formater une date au format français long (jj mois aaaa)
 * Exemple: "21 janvier 2025"
 */
export function formatDateLong(date: Date | string | number | null | undefined): string {
  if (!date) return '';

  const d = typeof date === 'string' || typeof date === 'number'
    ? new Date(date)
    : date;

  // Vérifier que la date est valide
  if (isNaN(d.getTime())) return '';

  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(d);
}

/**
 * Calcule le sous-total visuel pour une ligne SUBTOTAL.
 * Remonte les lignes ITEM précédentes jusqu'au dernier SECTION_TITLE
 * et somme leurs montants (quantity * unitPrice ou total).
 *
 * Utilisé par EditableItemsTable (édition) et ItemsTable (preview).
 * Ce calcul est purement visuel — le backend calcule les vrais totaux.
 */
export function calculateVisualSubtotal(
  items: Array<{ lineType?: string; quantity: number; unitPrice: number; total: number; subtotalAmount?: number }>,
  subtotalIndex: number
): number {
  // Utiliser subtotalAmount du backend si disponible
  const item = items[subtotalIndex];
  if (item?.subtotalAmount) return item.subtotalAmount;

  let sum = 0;
  for (let i = subtotalIndex - 1; i >= 0; i--) {
    if (items[i].lineType === 'SECTION_TITLE') break;
    if (!items[i].lineType || items[i].lineType === 'ITEM') {
      sum += items[i].total ?? (items[i].quantity * items[i].unitPrice);
    }
  }
  return sum;
}
